/* eslint-disable @typescript-eslint/indent */
import React, { ReactNode, Component } from 'react';
import styled from 'styled-components';
import { Query, QueryResult } from 'react-apollo';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  themes,
  utils,
  Patient,
  ReduxState,
  AppointmentType,
  AppointmentCategory
} from 'kb-shared';

import getCategoryFromQueryParams from './getCategoryFromQueryParams';
import SegmentedControl from '../../../../components/SegmentedControl';
import AppointmentListCard from '../../components/AppointmentListCard';
import MembershipDetailBlock from '../../components/MembershipDetailBlock';
import SkipToSignUpCTA from '../../components/SkipToSignUpCTA';
import { swipeLeft, swipeRight } from '../../../../assets/icons';

type AvailableAppointments = QueryResult<{
  appointmentTypes: Array<AppointmentType>;
  categories: Array<AppointmentCategory>;
}>;

type PatientResponse = QueryResult<{
  patient: Patient;
}>;

type Props = RouteComponentProps & {
  mobileLayout: boolean;
  selectedAppointment: AppointmentType | null;
  userLoggedIn: boolean;
  onSelect: (appointment: AppointmentType) => void;
  onSelectMembership: (membership: Membership) => void;
  onSkipToSignUp: () => void;
};

const APPOINTMENT_TYPES_AND_CATEGORIES = gql`
  query appointmentTypes {
    appointmentTypes {
      id
      name
      virtual
      virtualFert
      description
      videoLink
      rateCents
      category {
        id
        name
      }
    }

    categories {
      id
      name
      description
    }
  }
`;

export const PATIENT_MEMBERSHIP_QUERY = gql`
  query patientMembership {
    patient {
      id
      patientMembership {
        id
        membershipRules {
          code
          id
          discountId
        }
      }
    }
  }
`;

type Membership = {
  description: string;
  id: string;
  name: string;
  rateCents: number;
};

type MembershipQueryProps = QueryResult<{
  memberships: Membership[];
}>;

const MEMBERSHIPS = gql`
  query memberships {
    memberships {
      description
      id
      name
      rateCents
    }
  }
`;

interface MobileProps {
  mobile?: boolean;
}

const Container = styled.div`
  ${({ mobile }: MobileProps) => `
    padding-top: ${mobile ? '48px' : '0px'};
  `}

  @media screen and (max-width: ${themes.breakpoints.phone}) {
    max-width: ${themes.breakpoints.phone};
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const DescriptionText = styled.div`
  ${themes.fonts.FoundersGrotesk};
  color: ${themes.colors.KBNight};
  font-size: 14px;
  padding-top: 20px;
  padding-bottom: 12px;
  white-space: pre-wrap;

  @media screen and (max-width: ${themes.breakpoints.smallDesktop}) {
    margin: 0 15px;
  }
`;

const AppointmentCardsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  width: 100vw;
`;

const AppointmentCardWrapper = styled.div`
  flex: 1 0 auto;
  padding-bottom: 20px;
  ${({ mobile }: MobileProps) => `
    :first-child {
      padding-left: ${mobile ? '24px' : '0px'};
    };
    :last-child {
      padding-right: ${mobile ? '24px' : '0px'};
    }
    padding-left:  ${mobile ? '8px' : '0px'};
    padding-right: ${mobile ? '8px' : '0px'};
    cursor: ${mobile ? 'default' : 'pointer'};
    width: ${mobile ? '80%' : '100%'};
  `}
`;

const SkipToSignUpContainer = styled.div`
  padding-top: 24px;
  padding-bottom: 48px;
  ${({ mobile }: MobileProps) => `margin-bottom: ${mobile ? '25px' : '0px'};`};
`;

const MembershipDetailContainer = styled.div`
  border: solid 4px ${themes.colors.KBYellow};
  margin-top: 36px;
`;

const SwipeLeftIcon = styled.img`
  position: sticky;
  top: 50%;
  left: 0;
`;

const SwipeRightIcon = styled.img`
  position: sticky;
  top: 50%;
  right: 0;
  z-index: 1;
`;

type State = {
  selectedCategory: string;
};

const CATEGORIES = ['Fertility', 'Gyn', 'Coaching', 'LGBTQ'];

class SelectOption extends Component<Props, State> {
  scrollContainer: { current: null | HTMLDivElement };

  constructor(props: Props) {
    super(props);

    const selectedCategory = getCategoryFromQueryParams(CATEGORIES, props.location);

    this.state = {
      selectedCategory
    };
    this.scrollContainer = React.createRef();
  }

  renderMembershipBlock(category: AppointmentCategory, membership?: Membership) {
    // Only render membership CTA block if the members-only category is shown
    // @ts-ignore
    const requiresMembership = false;
    if (!requiresMembership) {
      return null;
    }

    return (
      <MembershipDetailContainer>
        <MembershipDetailBlock
          showBecomeMemberCTA={true}
          onBecomeMember={() => {
            if (membership) {
              this.props.onSelectMembership(membership);
            }
          }}
        />
      </MembershipDetailContainer>
    );
  }

  renderAppointments(
    appointments: AppointmentType[] | null,
    userHasMembership: boolean,
    membership?: Membership
  ) {
    if (!appointments) {
      return null;
    }

    const selectedAppointmentId =
      this.props.selectedAppointment && this.props.selectedAppointment.id;

    // TEMP: put virtual fertility first in list
    // @ts-ignore
    const sortedAppointments = appointments.sort(app => (app.virtualFert ? -1 : app.id));

    const appointmentCards: ReactNode[] = sortedAppointments.map((apt: AppointmentType) => {
      const requiresMembership = utils.isMembersOnly(
        parseInt(apt.category.id),
        process.env.REACT_APP_ENV
      );
      return (
        <AppointmentCardWrapper key={apt.id} mobile={this.props.mobileLayout}>
          <AppointmentListCard
            requiresMembership={requiresMembership}
            hasMembership={userHasMembership}
            onSelect={() => {
              //Only allow selection if membership is not required, or user is a member
              if (!requiresMembership || (requiresMembership && userHasMembership)) {
                this.props.onSelect(apt);
              } else {
                if (membership) {
                  this.props.onSelectMembership(membership);
                }
              }
            }}
            selected={selectedAppointmentId === apt.id}
            mobileLayout={this.props.mobileLayout}
            name={apt.name}
            price={`$${apt.rateCents / 100}`}
            description={apt.description}
          />
        </AppointmentCardWrapper>
      );
    });

    if (this.props.mobileLayout) {
      const scrollAmount = window.innerWidth * 0.8 + 16; // card + padding
      return (
        <AppointmentCardsContainer>
          <ScrollContainer ref={this.scrollContainer}>
            <SwipeLeftIcon
              src={swipeLeft}
              onClick={() => {
                if (this.scrollContainer.current) {
                  this.scrollContainer.current.scrollLeft -= scrollAmount;
                }
              }}
            />
            {appointmentCards}
            <SwipeRightIcon
              src={swipeRight}
              onClick={() => {
                if (this.scrollContainer.current) {
                  this.scrollContainer.current.scrollLeft += scrollAmount;
                }
              }}
            />
          </ScrollContainer>
          {!this.props.userLoggedIn && (
            <SkipToSignUpContainer mobile={this.props.mobileLayout}>
              <SkipToSignUpCTA
                mobileLayout={this.props.mobileLayout}
                onClick={this.props.onSkipToSignUp}
              />
            </SkipToSignUpContainer>
          )}
        </AppointmentCardsContainer>
      );
    } else {
      return appointmentCards;
    }
  }

  render() {
    return (
      <Container>
        <Content>
          <Query query={APPOINTMENT_TYPES_AND_CATEGORIES}>
            {({ data }: AvailableAppointments) => {
              const appointments = data && data.appointmentTypes;
              const categories = data && data.categories;
              if (!appointments || !categories) {
                return null;
              }

              // Categorize Appointments by their category
              const categorizedAppointments = appointments.reduce((map, apt: AppointmentType) => {
                // @ts-ignore
                let categorized = map[apt.category.name];
                if (!categorized) {
                  categorized = {
                    category: apt.category,
                    appointments: []
                  };
                }
                categorized.appointments.push(apt);
                // @ts-ignore
                map[apt.category.name] = categorized;
                return map;
              }, {});

              const segmentedControlOptions = categories
                .filter(
                  // @ts-ignore
                  category => categorizedAppointments[category.name] // filter out categories with no appointments
                )
                .map(category => category.name);

              const { selectedCategory } = this.state;
              const categoryDetail = categories.find(
                category => category.name === selectedCategory
              );

              // Grab the appointments for the currently selected category
              // @ts-ignore
              const selectedAppointments = categorizedAppointments[selectedCategory].appointments;

              return (
                <Query query={PATIENT_MEMBERSHIP_QUERY}>
                  {({ data }: PatientResponse) => {
                    const patientHasMembership =
                      (data &&
                        data.patient &&
                        // @ts-ignore
                        data.patient.patientMembership &&
                        // @ts-ignore
                        data.patient.patientMembership.id !== null) ||
                      false;

                    return (
                      <>
                        <SegmentedControl
                          mobile={this.props.mobileLayout}
                          options={segmentedControlOptions}
                          extractTitle={item => item}
                          selectedTitle={selectedCategory}
                          onSelect={item => {
                            this.setState({
                              selectedCategory: item
                            });
                          }}
                        />
                        <TextContainer>
                          <DescriptionText>{categoryDetail?.description}</DescriptionText>
                        </TextContainer>

                        <Query query={MEMBERSHIPS}>
                          {({ data }: MembershipQueryProps) => {
                            const membership = data && data.memberships && data.memberships[0];
                            return (
                              <div>
                                {this.renderAppointments(
                                  selectedAppointments,
                                  patientHasMembership,
                                  membership
                                )}
                                {!patientHasMembership
                                  ? this.renderMembershipBlock(
                                      // @ts-ignore
                                      selectedCategory,
                                      membership
                                    )
                                  : null}
                              </div>
                            );
                          }}
                        </Query>
                      </>
                    );
                  }}
                </Query>
              );
            }}
          </Query>
        </Content>
      </Container>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  userLoggedIn: state.patient.isLoggedIn
});

export default withRouter(connect(mapStateToProps)(SelectOption));
